const $ = (selector, scope = document) => scope.querySelector(selector);
const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const reducedMotion = reducedMotionQuery.matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;
reducedMotionQuery.addEventListener?.('change', () => window.location.reload());

/* Header and mobile navigation ------------------------------------------------ */

const header = $('[data-header]');
const menuToggle = $('[data-menu-toggle]');
const mobileNav = $('[data-mobile-nav]');
const mobileBuy = $('[data-mobile-buy]');

const setMenuOpen = (open) => {
  header?.classList.toggle('menu-open', open);
  document.body.classList.toggle('menu-open', open);
  if (mobileNav) mobileNav.inert = !open;
  menuToggle?.setAttribute('aria-expanded', String(open));
  menuToggle?.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
};

menuToggle?.addEventListener('click', () => setMenuOpen(!header.classList.contains('menu-open')));
$$('a', mobileNav).forEach((link) => link.addEventListener('click', () => setMenuOpen(false)));

const desktopLayoutQuery = window.matchMedia('(min-width: 821px)');
desktopLayoutQuery.addEventListener?.('change', (event) => {
  if (event.matches) setMenuOpen(false);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && header?.classList.contains('menu-open')) {
    setMenuOpen(false);
    menuToggle?.focus();
  }
});

/* Reveal choreography ---------------------------------------------------------- */

if (reducedMotion) {
  $$('.reveal').forEach((element) => element.classList.add('is-visible'));
} else {
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -9% 0px', threshold: 0.07 });

  $$('.reveal').forEach((element) => revealObserver.observe(element));
}

/* Continuous chapter system --------------------------------------------------- */

const sceneSections = $$('[data-scene]');
const chapterHud = $('[data-chapter-hud]');
const chapterIndex = $('[data-chapter-index]');
const chapterTitle = $('[data-chapter-title]');
const chapterProgress = $('[data-chapter-progress]');
const sceneRail = $('[data-scene-rail]');
const signalCanvas = $('[data-signal-canvas]');
const desktopNavLinks = $$('.desktop-nav a');
let activeScene = null;
let signalRgb = [90, 217, 255];
let chapterPeekTimer;
let signalSceneTimer;

const colorToRgb = (hex = '#5ad9ff') => {
  const clean = hex.replace('#', '');
  const normalized = clean.length === 3 ? clean.split('').map((char) => char + char).join('') : clean;
  const parsed = Number.parseInt(normalized, 16);
  return Number.isNaN(parsed) ? [90, 217, 255] : [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
};

const railButtons = sceneSections.map((section) => {
  if (!sceneRail) return null;
  const button = document.createElement('button');
  button.type = 'button';
  button.tabIndex = -1;
  button.setAttribute('aria-label', `Go to ${section.dataset.sceneTitle}`);
  button.title = section.dataset.sceneTitle;
  button.addEventListener('click', () => section.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' }));
  sceneRail.append(button);
  return button;
});

const setActiveScene = (section, initial = false) => {
  if (!section || activeScene === section) return;
  activeScene = section;
  const accent = section.dataset.sceneAccent || '#5ad9ff';
  const tone = section.dataset.sceneTone || 'dark';
  signalRgb = colorToRgb(accent);
  document.documentElement.style.setProperty('--scene-accent', accent);
  document.body.dataset.sceneTone = tone;

  if (chapterIndex) chapterIndex.textContent = section.dataset.sceneIndex || '00';
  if (chapterTitle) chapterTitle.textContent = section.dataset.sceneTitle || 'Awaakii';
  chapterHud?.classList.remove('is-updating');
  void chapterHud?.offsetWidth;
  chapterHud?.classList.add('is-updating');
  chapterHud?.classList.add('is-visible');
  sceneRail?.classList.add('is-peeking');
  window.clearTimeout(chapterPeekTimer);
  chapterPeekTimer = window.setTimeout(() => {
    chapterHud?.classList.remove('is-visible');
    sceneRail?.classList.remove('is-peeking');
  }, initial ? 2800 : 1550);

  const navMap = {
    top: 'top', difference: 'top', experience: 'top', health: 'top',
    sensing: 'top', sleep: 'top', neuro: 'top', loop: 'top', modes: 'top',
    design: 'top', specs: 'top', reserve: 'top', faq: 'faq'
  };
  const currentNav = navMap[section.id];
  desktopNavLinks.forEach((link) => {
    const selected = link.getAttribute('href') === `#${currentNav}`;
    link.classList.toggle('is-current', selected);
    if (selected) link.setAttribute('aria-current', 'location');
    else link.removeAttribute('aria-current');
  });

  sceneSections.forEach((item, index) => {
    const selected = item === section;
    item.classList.toggle('is-scene-active', selected);
    railButtons[index]?.classList.toggle('is-active', selected);
    if (selected) railButtons[index]?.setAttribute('aria-current', 'true');
    else railButtons[index]?.removeAttribute('aria-current');
  });

  if (!initial && !reducedMotion) {
    signalCanvas?.classList.add('is-engaged');
    window.clearTimeout(signalSceneTimer);
    signalSceneTimer = window.setTimeout(() => signalCanvas?.classList.remove('is-engaged'), 900);
  }
};

if (sceneSections.length) {
  setActiveScene(sceneSections[0], true);
  const sceneObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) setActiveScene(entry.target);
    });
  }, { rootMargin: '-43% 0px -46% 0px', threshold: 0 });
  sceneSections.forEach((section) => sceneObserver.observe(section));
}

/* A subtle signal field connects every scene ---------------------------------- */

if (signalCanvas && !reducedMotion) {
  const context = signalCanvas.getContext('2d', { alpha: true });
  let signalWidth = 0;
  let signalHeight = 0;
  let signalNodes = [];
  let signalFrame = 0;
  let signalPointerX = -1000;
  let signalPointerY = -1000;
  let signalPointerActive = false;
  let signalIdleTimer;
  let lastSignalDraw = 0;

  const createSignalNodes = () => {
    const count = signalWidth > 820 ? 24 : 12;
    signalNodes = Array.from({ length: count }, (_, index) => ({
      x: ((index * 67) % 97) / 97 * signalWidth,
      y: ((index * 43 + 17) % 89) / 89 * signalHeight,
      vx: Math.sin(index * 1.7) * .075,
      vy: Math.cos(index * 1.31) * .065,
      phase: index * .73
    }));
  };

  const resizeSignalCanvas = () => {
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    signalWidth = window.innerWidth;
    signalHeight = window.innerHeight;
    signalCanvas.width = Math.round(signalWidth * ratio);
    signalCanvas.height = Math.round(signalHeight * ratio);
    signalCanvas.style.width = `${signalWidth}px`;
    signalCanvas.style.height = `${signalHeight}px`;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    createSignalNodes();
  };

  const drawSignalField = (time = 0) => {
    if (signalWidth <= 820 && time - lastSignalDraw < 32) {
      signalFrame = window.requestAnimationFrame(drawSignalField);
      return;
    }
    lastSignalDraw = time;
    context.clearRect(0, 0, signalWidth, signalHeight);
    const [red, green, blue] = signalRgb;
    const connectionDistance = signalWidth > 820 ? 185 : 135;

    signalNodes.forEach((node) => {
      if (signalPointerActive) {
        const dx = signalPointerX - node.x;
        const dy = signalPointerY - node.y;
        const distance = Math.hypot(dx, dy);
        if (distance < 190 && distance > 1) {
          const pull = (1 - distance / 190) * .0035;
          node.vx += dx * pull * .018;
          node.vy += dy * pull * .018;
        }
      }

      node.vx *= .995;
      node.vy *= .995;
      node.x += node.vx + Math.sin(time * .00018 + node.phase) * .035;
      node.y += node.vy + Math.cos(time * .00015 + node.phase) * .03;
      if (node.x < -20) node.x = signalWidth + 20;
      if (node.x > signalWidth + 20) node.x = -20;
      if (node.y < -20) node.y = signalHeight + 20;
      if (node.y > signalHeight + 20) node.y = -20;
    });

    for (let first = 0; first < signalNodes.length; first += 1) {
      for (let second = first + 1; second < signalNodes.length; second += 1) {
        const a = signalNodes[first];
        const b = signalNodes[second];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (distance > connectionDistance) continue;
        const alpha = (1 - distance / connectionDistance) * .42;
        context.beginPath();
        context.moveTo(a.x, a.y);
        context.lineTo(b.x, b.y);
        context.strokeStyle = `rgba(${red},${green},${blue},${alpha})`;
        context.lineWidth = .7;
        context.stroke();
      }
    }

    signalNodes.forEach((node, index) => {
      const pulse = .55 + Math.sin(time * .0012 + node.phase) * .28;
      context.beginPath();
      context.arc(node.x, node.y, index % 5 === 0 ? 2.1 : 1.15, 0, Math.PI * 2);
      context.fillStyle = `rgba(${red},${green},${blue},${pulse})`;
      context.fill();
    });

    if (signalPointerActive && finePointer) {
      const radius = 28 + Math.sin(time * .003) * 3;
      context.beginPath();
      context.arc(signalPointerX, signalPointerY, radius, 0, Math.PI * 2);
      context.strokeStyle = `rgba(${red},${green},${blue},.25)`;
      context.lineWidth = .7;
      context.stroke();
    }

    signalFrame = window.requestAnimationFrame(drawSignalField);
  };

  resizeSignalCanvas();
  signalFrame = window.requestAnimationFrame(drawSignalField);
  window.addEventListener('resize', resizeSignalCanvas, { passive: true });
  window.addEventListener('pointermove', (event) => {
    signalPointerX = event.clientX;
    signalPointerY = event.clientY;
    signalPointerActive = true;
    signalCanvas.classList.add('is-engaged');
    window.clearTimeout(signalIdleTimer);
    signalIdleTimer = window.setTimeout(() => {
      signalPointerActive = false;
      signalCanvas.classList.remove('is-engaged');
    }, 760);
  }, { passive: true });
  document.addEventListener('pointerleave', () => {
    signalPointerActive = false;
    signalCanvas.classList.remove('is-engaged');
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) window.cancelAnimationFrame(signalFrame);
    else signalFrame = window.requestAnimationFrame(drawSignalField);
  });
}

/* One requestAnimationFrame pipeline for every scroll-linked effect ------------ */

const progressBar = $('[data-page-progress]');
const hero = $('.hero');
const heroMedia = $('.hero-media');
const heroContent = $('.hero-content');
const heroFloat = $('[data-hero-float]');
const neuroSection = $('#neuro');
const neuroStage = $('[data-neuro-stage]');
const neuroRing = $('[data-neuro-ring]');
const neuroPhaseIndex = $('[data-neuro-phase-index]');
const neuroPhaseTitle = $('[data-neuro-phase-title]');
const neuroPhaseCopy = $('[data-neuro-phase-copy]');
const neuroPhaseReadout = $('.neuro-phase-readout');
const loopSection = $('#loop');
const loopVisual = $('[data-loop-visual]');
const loopSteps = $$('[data-loop-step]');
const loopDots = loopVisual ? $$('[data-loop-dot]', loopVisual) : [];
const parallaxElements = $$('[data-parallax]');
let scrollFrameRequested = false;
let lastScrollY = window.scrollY;
let activeNeuroPhase = 'detect';
let neuroCueTimer;
let activeLoopStep = -1;

const setLoopStep = (index) => {
  if (!loopSteps.length) return;
  const nextIndex = Math.max(0, Math.min(loopSteps.length - 1, index));
  if (nextIndex === activeLoopStep) return;
  activeLoopStep = nextIndex;
  loopVisual?.style.setProperty('--loop-step', String(nextIndex));
  loopVisual?.setAttribute('data-loop-state', String(nextIndex + 1));
  loopSteps.forEach((step, stepIndex) => {
    step.classList.toggle('is-active', stepIndex === nextIndex);
    step.classList.toggle('is-past', stepIndex < nextIndex);
  });
  loopDots.forEach((dot, dotIndex) => {
    dot.classList.toggle('is-active', dotIndex === nextIndex);
    dot.classList.toggle('is-past', dotIndex < nextIndex);
  });
  if (!reducedMotion && loopVisual) {
    loopVisual.classList.remove('is-shifting');
    void loopVisual.offsetWidth;
    loopVisual.classList.add('is-shifting');
  }
};

const neuroPhases = {
  detect: {
    index: '01 / Incoming call',
    title: 'A call reaches your hand',
    copy: 'A distinct vibration cue helps you notice it without checking your phone.'
  },
  decide: {
    index: '02 / Message alert',
    title: 'Messages stay discreet',
    copy: 'Selected app notifications arrive as a quiet pattern on Ring One.'
  },
  intervene: {
    index: '03 / Health reminder',
    title: 'A timely wellness cue',
    copy: 'Heart-rate, temperature or stress trends can trigger a gentle reminder.'
  }
};

const setNeuroPhase = (key) => {
  if (!neuroStage || key === activeNeuroPhase) return;
  activeNeuroPhase = key;
  const phase = neuroPhases[key];
  neuroStage.dataset.neuroPhase = key;
  if (neuroPhaseIndex) neuroPhaseIndex.textContent = phase.index;
  if (neuroPhaseTitle) neuroPhaseTitle.textContent = phase.title;
  if (neuroPhaseCopy) neuroPhaseCopy.textContent = phase.copy;
  neuroPhaseReadout?.classList.remove('is-updating');
  void neuroPhaseReadout?.offsetWidth;
  neuroPhaseReadout?.classList.add('is-updating');
  neuroStage.classList.remove('is-cueing');
  void neuroStage.offsetWidth;
  neuroStage.classList.add('is-cueing');
  window.clearTimeout(neuroCueTimer);
  neuroCueTimer = window.setTimeout(() => neuroStage.classList.remove('is-cueing'), 1800);
};

const updateScrollMotion = () => {
  scrollFrameRequested = false;
  const scrollY = window.scrollY;
  const viewportHeight = window.innerHeight;
  const scrollable = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
  const pageProgress = clamp(scrollY / scrollable);

  progressBar?.style.setProperty('transform', `scaleY(${pageProgress})`);
  header?.classList.toggle('is-scrolled', scrollY > 48);
  const scrollDelta = scrollY - lastScrollY;
  if (header && !header.classList.contains('menu-open')) {
    if (scrollY <= 80) {
      header.classList.remove('is-hidden');
      lastScrollY = scrollY;
    } else if (Math.abs(scrollDelta) >= 14) {
      header.classList.toggle('is-hidden', scrollDelta > 0 && scrollY > 150);
      lastScrollY = scrollY;
    }
  }
  mobileBuy?.classList.toggle('is-visible', scrollY > viewportHeight * .8);

  if (activeScene && chapterProgress) {
    const rect = activeScene.getBoundingClientRect();
    const sceneProgress = clamp((viewportHeight * .58 - rect.top) / Math.max(rect.height, 1));
    chapterProgress.style.transform = `scaleX(${sceneProgress})`;
  }

  if (!reducedMotion && window.innerWidth > 820) {
    parallaxElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;
      const speed = Number.parseFloat(element.dataset.parallax || '0.04');
      const centerDelta = clamp((viewportHeight * .5 - (rect.top + rect.height * .5)) / viewportHeight, -1, 1);
      element.style.setProperty('--parallax-y', `${centerDelta * viewportHeight * speed}px`);
    });
  }

  if (!reducedMotion && heroMedia && scrollY < viewportHeight * 1.2) {
    const heroProgress = clamp(scrollY / viewportHeight);
    heroMedia.style.setProperty('--hero-media-y', `${heroProgress * 42}px`);
    if (heroContent) {
      heroContent.style.transform = `translate3d(0, ${heroProgress * -18}px, 0)`;
      heroContent.style.opacity = String(1 - heroProgress * .55);
    }
    if (heroFloat) {
      heroFloat.style.setProperty('--float-scroll-y', `${heroProgress * 56}px`);
      heroFloat.style.setProperty('--float-scale', String(1 - heroProgress * .14));
      heroFloat.style.setProperty('--float-opacity', String(1 - heroProgress * 1.05));
    }
  }

  if (!reducedMotion && neuroSection && neuroStage && neuroRing && window.innerWidth > 820) {
    const rect = neuroSection.getBoundingClientRect();
    const travel = Math.max(1, neuroSection.offsetHeight - viewportHeight);
    const progress = clamp(-rect.top / travel);
    neuroRing.style.setProperty('--ring-rotate', `${32 - progress * 66}deg`);
    neuroRing.style.setProperty('--ring-scale', String(.9 + progress * .15));
    neuroRing.style.setProperty('--ring-y', `${24 - progress * 48}px`);
    const phase = progress < .32 ? 'detect' : progress < .68 ? 'decide' : 'intervene';
    setNeuroPhase(phase);
    neuroStage.classList.toggle('is-energized', phase === 'intervene' && progress < .96);
  }

  if (loopSection && loopVisual && loopSteps.length) {
    const rect = loopSection.getBoundingClientRect();
    const progress = clamp((viewportHeight * .72 - rect.top) / Math.max(rect.height, 1));
    if (!reducedMotion) {
      loopVisual.style.setProperty('--loop-rotate', `${progress * 42}deg`);
      loopVisual.style.setProperty('--loop-progress', String(progress));
      loopVisual.style.setProperty('--loop-sweep', `${progress * 360}deg`);
    }
    const activeIndex = Math.min(loopSteps.length - 1, Math.floor(progress * loopSteps.length));
    setLoopStep(activeIndex);
  }
};

const requestScrollMotion = () => {
  if (scrollFrameRequested) return;
  scrollFrameRequested = true;
  window.requestAnimationFrame(updateScrollMotion);
};

updateScrollMotion();
window.addEventListener('scroll', requestScrollMotion, { passive: true });
window.addEventListener('resize', requestScrollMotion, { passive: true });

setLoopStep(0);

if (loopVisual && finePointer && !reducedMotion) {
  let loopPointerFrame = 0;
  let loopPointerX = 0;
  let loopPointerY = 0;
  let loopPointerRect = null;
  const renderLoopPointer = () => {
    loopPointerFrame = 0;
    loopVisual.style.setProperty('--loop-tilt-x', `${loopPointerY * -9}deg`);
    loopVisual.style.setProperty('--loop-tilt-y', `${loopPointerX * 11}deg`);
    loopVisual.style.setProperty('--loop-light-x', `${50 + loopPointerX * 42}%`);
    loopVisual.style.setProperty('--loop-light-y', `${50 + loopPointerY * 42}%`);
  };
  const requestLoopPointer = () => {
    if (!loopPointerFrame) loopPointerFrame = window.requestAnimationFrame(renderLoopPointer);
  };
  loopVisual.addEventListener('pointerenter', () => {
    loopPointerRect = loopVisual.getBoundingClientRect();
    loopVisual.classList.add('is-pointer-active');
  }, { passive: true });
  loopVisual.addEventListener('pointermove', (event) => {
    const rect = loopPointerRect || loopVisual.getBoundingClientRect();
    loopPointerX = (event.clientX - rect.left) / rect.width - .5;
    loopPointerY = (event.clientY - rect.top) / rect.height - .5;
    requestLoopPointer();
  }, { passive: true });
  loopVisual.addEventListener('pointerleave', () => {
    loopPointerX = 0;
    loopPointerY = 0;
    loopPointerRect = null;
    loopVisual.classList.remove('is-pointer-active');
    requestLoopPointer();
  });
}

loopSteps.forEach((step, index) => {
  step.addEventListener('pointerenter', () => setLoopStep(index), { passive: true });
  step.addEventListener('click', () => setLoopStep(index));
});

/* Hero depth and floating UI --------------------------------------------------- */

if (!reducedMotion && finePointer && hero && heroMedia) {
  let heroPointerFrame = 0;
  let heroPointerX = 0;
  let heroPointerY = 0;

  const renderHeroPointer = () => {
    heroPointerFrame = 0;
    heroMedia.style.setProperty('--hero-x', `${heroPointerX * -12}px`);
    heroMedia.style.setProperty('--hero-y', `${heroPointerY * -8}px`);
    heroMedia.style.setProperty('--hero-roll', `${-3.5 + heroPointerX * 2.4}deg`);
    heroFloat?.style.setProperty('--float-x', `${heroPointerX * -9}px`);
    heroFloat?.style.setProperty('--float-y', `${heroPointerY * -8}px`);
  };

  const requestHeroPointer = () => {
    if (!heroPointerFrame) heroPointerFrame = window.requestAnimationFrame(renderHeroPointer);
  };

  hero.addEventListener('pointermove', (event) => {
    const rect = hero.getBoundingClientRect();
    heroPointerX = (event.clientX - rect.left) / rect.width - .5;
    heroPointerY = (event.clientY - rect.top) / rect.height - .5;
    requestHeroPointer();
  }, { passive: true });

  hero.addEventListener('pointerleave', () => {
    heroPointerX = 0;
    heroPointerY = 0;
    requestHeroPointer();
  });
}

/* Ring One finish selector ----------------------------------------------------- */

const ringColorButtons = $$('[data-ring-src]');
const ringVisuals = $$('[data-ring-finish-target]');
const ringFinishLabels = $$('[data-ring-finish]');
const heroProductLinks = $$('[data-hero-product-link]');
let ringColorRequest = 0;

const setRingColor = (button, animate = true) => {
  if (!button) return;
  const source = button.dataset.ringSrc;
  const label = button.dataset.ringLabel || 'selected finish';
  const color = button.dataset.ringColor;
  const requestId = ++ringColorRequest;
  if (!source) return;

  ringColorButtons.forEach((item) => {
    const selected = item === button;
    item.classList.toggle('is-active', selected);
    item.setAttribute('aria-pressed', String(selected));
  });

  if (color) {
    document.documentElement.style.setProperty('--ring-color', color);
    document.body.style.setProperty('--ring-color', color);
  }
  ringFinishLabels.forEach((element) => { element.textContent = label; });
  if (button.dataset.ringVariantUrl) {
    heroProductLinks.forEach((link) => { link.href = button.dataset.ringVariantUrl; });
  }

  ringVisuals.forEach((visual) => {
    const hiddenFromAssistiveTech = visual.matches('[aria-hidden="true"]') || Boolean(visual.closest('[aria-hidden="true"]'));
    const updateVisual = () => {
      visual.src = source;
      visual.alt = hiddenFromAssistiveTech ? '' : `${label} Awaakii Ring One`;
    };

    if (animate && !reducedMotion) {
      visual.classList.add('is-switching');
      window.setTimeout(() => {
        if (requestId !== ringColorRequest) return;
        updateVisual();
        window.requestAnimationFrame(() => visual.classList.remove('is-switching'));
      }, 150);
    } else {
      updateVisual();
    }
  });
};

ringColorButtons.forEach((button) => button.addEventListener('click', () => setRingColor(button)));

const initialRingColor = ringColorButtons.find((button) => (
  button.classList.contains('is-active') || button.getAttribute('aria-pressed') === 'true'
)) || ringColorButtons[0];
setRingColor(initialRingColor, false);

const preloadRingFinishes = () => {
  ringColorButtons.forEach((button) => {
    if (button === initialRingColor || !button.dataset.ringSrc) return;
    const image = new Image();
    image.src = button.dataset.ringSrc;
    image.decode?.().catch(() => {});
  });
};

if ('requestIdleCallback' in window) window.requestIdleCallback(preloadRingFinishes, { timeout: 2200 });
else window.setTimeout(preloadRingFinishes, 900);

/* Premium card tilt ------------------------------------------------------------ */

$$('[data-tilt-card]').forEach((card) => {
  if (reducedMotion || !finePointer) return;
  card.addEventListener('pointermove', (event) => {
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    card.style.setProperty('--tilt-x', `${(y - .5) * -3.2}deg`);
    card.style.setProperty('--tilt-y', `${(x - .5) * 3.2}deg`);
    card.style.setProperty('--spot-x', `${x * 100}%`);
    card.style.setProperty('--spot-y', `${y * 100}%`);
  });
  card.addEventListener('pointerleave', () => {
    card.style.setProperty('--tilt-x', '0deg');
    card.style.setProperty('--tilt-y', '0deg');
    card.style.setProperty('--spot-x', '50%');
    card.style.setProperty('--spot-y', '50%');
  });
});

/* Ring One wellness trend interaction ----------------------------------------- */

const healthStates = {
  heart: {
    label: 'Live heart-rate trend',
    status: 'Synced · now',
    question: 'Heart-rate overview',
    message: 'Your resting heart-rate trend is close to your 7-day baseline. A short rise after activity returned toward your usual range.',
    metrics: [['68 bpm', 'Resting'], ['67 bpm', '7-day average'], ['42 ms', 'HRV trend']],
    action: 'Refresh heart-rate trend',
    syncing: 'Reading recent pulse data…',
    synced: 'Heart-rate trend refreshed'
  },
  sleep: {
    label: 'Last night · sleep stages',
    status: 'Synced · 08:12',
    question: 'Last night’s sleep',
    message: 'You slept 18 minutes longer than your 7-night average. Bedtime remained close to your recent routine.',
    metrics: [['7h 42m', 'Total sleep'], ['86', 'Sleep score'], ['91%', 'Bedtime consistency']],
    action: 'Refresh sleep summary',
    syncing: 'Reading last night’s sleep…',
    synced: 'Sleep summary refreshed'
  },
  oxygen: {
    label: 'Overnight SpO₂ trend',
    status: 'Synced · overnight',
    question: 'Blood-oxygen overview',
    message: 'Eligible overnight readings stayed within your recent personal range. Gaps may appear when movement or fit affects sensing.',
    metrics: [['97%', 'Overnight average'], ['95–99%', 'Overnight range'], ['Stable', 'Recent trend']],
    action: 'Refresh SpO₂ trend',
    syncing: 'Reading eligible SpO₂ data…',
    synced: 'Blood-oxygen trend refreshed'
  },
  temperature: {
    label: 'Temperature · baseline',
    status: 'Synced · today',
    question: 'Temperature overview',
    message: 'Your latest temperature trend is +0.2°C from your recent baseline and remains inside your configured wellness range.',
    metrics: [['+0.2°C', 'From baseline'], ['+0.1°C', '7-day average'], ['Within', 'Configured range']],
    action: 'Refresh temperature trend',
    syncing: 'Comparing temperature trends…',
    synced: 'Temperature trend refreshed'
  }
};

const agentDemo = $('[data-agent-demo]');
const agentMessage = agentDemo ? $('[data-agent-message]', agentDemo) : null;
const agentStateLabel = agentDemo ? $('[data-agent-state-label]', agentDemo) : null;
const agentStatus = agentDemo ? $('[data-agent-status]', agentDemo) : null;
const agentQuestion = agentDemo ? $('[data-agent-question]', agentDemo) : null;
const agentActionLabel = agentDemo ? $('[data-agent-action-label]', agentDemo) : null;
const agentMetricValues = agentDemo ? $$('[data-agent-metric-value]', agentDemo) : [];
const agentMetricLabels = agentDemo ? $$('[data-agent-metric-label]', agentDemo) : [];
const agentStateSteps = agentDemo ? $$('[data-agent-state-step]', agentDemo) : [];
const agentButtons = $$('[data-agent-step]');
const demoAction = agentDemo ? $('.demo-action', agentDemo) : null;
let activeAgentState = agentDemo?.dataset.agentState || 'heart';
let agentTimer;
let demoActionMidTimer;
let demoActionTimer;

const animateAgentNode = (node, delay = 0) => {
  if (!node || reducedMotion) return;
  node.animate(
    [
      { opacity: 0, transform: 'translateY(9px)', filter: 'blur(3px)' },
      { opacity: 1, transform: 'none', filter: 'none' }
    ],
    { duration: 520, delay, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'both' }
  );
};

const renderAgentState = (key, animate = true) => {
  const state = healthStates[key];
  if (!agentDemo || !state) return;
  activeAgentState = key;
  agentDemo.dataset.agentState = key;
  agentDemo.setAttribute('aria-labelledby', `health-tab-${key}`);
  agentDemo.classList.remove('is-processing', 'is-syncing', 'is-complete');

  if (agentStateLabel) agentStateLabel.textContent = state.label;
  if (agentStatus) agentStatus.textContent = state.status;
  if (agentQuestion) agentQuestion.textContent = state.question;
  if (agentMessage) agentMessage.textContent = state.message;
  if (agentActionLabel) agentActionLabel.textContent = state.action;

  state.metrics.forEach(([value, label], index) => {
    if (agentMetricValues[index]) agentMetricValues[index].textContent = value;
    if (agentMetricLabels[index]) agentMetricLabels[index].textContent = label;
  });

  agentButtons.forEach((button) => {
    const selected = button.dataset.agentStep === key;
    button.classList.toggle('is-active', selected);
    button.setAttribute('aria-selected', String(selected));
    button.tabIndex = selected ? 0 : -1;
  });
  agentStateSteps.forEach((step) => {
    step.classList.toggle('is-active', step.dataset.agentStateStep === key);
    step.classList.remove('is-complete');
  });

  window.clearTimeout(agentTimer);
  if (!animate || reducedMotion) return;
  void agentDemo.offsetWidth;
  agentDemo.classList.add('is-processing');
  animateAgentNode(agentStateLabel);
  animateAgentNode(agentStatus, 35);
  animateAgentNode(agentQuestion, 60);
  agentMetricValues.forEach((node, index) => animateAgentNode(node.parentElement, 80 + index * 75));
  animateAgentNode(agentMessage, 150);
  animateAgentNode(agentActionLabel, 190);
  agentTimer = window.setTimeout(() => agentDemo.classList.remove('is-processing'), 1150);
};

const activateAgentButton = (button, focus = false) => {
  if (!button || !healthStates[button.dataset.agentStep]) return;
  window.clearTimeout(demoActionMidTimer);
  window.clearTimeout(demoActionTimer);
  demoAction?.classList.remove('is-running', 'is-complete');
  renderAgentState(button.dataset.agentStep);
  if (focus) button.focus();
};

agentButtons.forEach((button, index) => {
  button.addEventListener('click', () => activateAgentButton(button));
  button.addEventListener('keydown', (event) => {
    let nextIndex = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % agentButtons.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + agentButtons.length) % agentButtons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = agentButtons.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    activateAgentButton(agentButtons[nextIndex], true);
  });
});

demoAction?.addEventListener('click', () => {
  if (demoAction.classList.contains('is-running')) return;
  const state = healthStates[activeAgentState];
  if (!state) return;
  window.clearTimeout(demoActionMidTimer);
  window.clearTimeout(demoActionTimer);
  demoAction.classList.remove('is-complete');
  demoAction.classList.add('is-running');
  agentDemo?.classList.add('is-syncing');
  if (agentActionLabel) agentActionLabel.textContent = state.syncing;
  if (agentStatus) agentStatus.textContent = 'Syncing…';

  demoActionMidTimer = window.setTimeout(() => {
    if (agentActionLabel) agentActionLabel.textContent = `Comparing ${activeAgentState === 'sleep' ? '7 nights' : '7-day trend'}…`;
  }, reducedMotion ? 0 : 650);

  demoActionTimer = window.setTimeout(() => {
    demoAction.classList.remove('is-running');
    demoAction.classList.add('is-complete');
    agentDemo?.classList.remove('is-syncing');
    agentDemo?.classList.add('is-complete');
    if (agentActionLabel) agentActionLabel.textContent = state.synced;
    if (agentStatus) agentStatus.textContent = 'Updated just now';
  }, reducedMotion ? 160 : 2100);
});

renderAgentState(activeAgentState, false);

/* Touch-control mode transitions ---------------------------------------------- */

const modes = {
  music: {
    index: '01 / Music', title: 'Music, controlled from your finger.',
    copy: 'Use the touch surface to play, pause, skip forward or return to the previous track without reaching for your phone.',
    duration: '1 TAP', previewMs: 1450, vibration: [18, 70, 18], image: window.AWAAKII_THEME?.assets?.graphite || '', accent: '#6dc5df', state: 'music',
    alt: 'Graphite Awaakii Ring One for touch music control'
  },
  camera: {
    index: '02 / Camera', title: 'Take the shot with a tap.',
    copy: 'Trigger a photo or start video recording remotely, making group shots and hands-free framing easier.',
    duration: 'REMOTE', previewMs: 1150, vibration: [28], image: window.AWAAKII_THEME?.assets?.silver || '', accent: '#93abb3', state: 'camera',
    alt: 'Silver Awaakii Ring One for remote camera control'
  },
  video: {
    index: '03 / Short video', title: 'Scroll without holding the screen.',
    copy: 'Move through compatible short-video apps with play, pause and next controls directly from the ring.',
    duration: 'SWIPE', previewMs: 1350, vibration: [14, 54, 14], image: window.AWAAKII_THEME?.assets?.obsidian || '', accent: '#5d8ea8', state: 'video',
    alt: 'Obsidian Awaakii Ring One for short-video control'
  },
  slides: {
    index: '04 / Slides', title: 'Advance the room from your hand.',
    copy: 'Move to the previous or next presentation slide while staying focused on the people in front of you.',
    duration: 'PRESENT', previewMs: 1250, vibration: [16, 62, 16], image: window.AWAAKII_THEME?.assets?.roseGold || '', accent: '#6fa9a1', state: 'slides',
    alt: 'Rose gold Awaakii Ring One for presentation control'
  }
};

const preloadModeImages = () => Object.values(modes).forEach((mode) => {
  const image = new Image();
  image.src = mode.image;
  image.decode?.().catch(() => {});
});

if ('requestIdleCallback' in window) window.requestIdleCallback(preloadModeImages, { timeout: 3000 });
else window.setTimeout(preloadModeImages, 1300);

const modePanel = $('[data-mode-panel]');
const modeImage = $('[data-mode-image]');
const modeIndex = $('[data-mode-index]');
const modeTitle = $('[data-mode-title]');
const modeCopy = $('[data-mode-copy]');
const modeDuration = $('[data-mode-duration]');
const modeStatus = $('[data-mode-status]');
const modeTabs = $$('[data-mode]');
let modeTimer;
let modeSwapTimer;
let modeRequest = 0;

const renderMode = (mode) => {
  modeImage.src = mode.image;
  modeImage.alt = mode.alt;
  modeIndex.textContent = mode.index;
  modeTitle.textContent = mode.title;
  modeCopy.textContent = mode.copy;
  modeDuration.textContent = mode.duration;
  modePanel.dataset.modeState = mode.state;
  modePanel.style.setProperty('--mode-accent', mode.accent);
  if (modeStatus) modeStatus.textContent = 'Ready to test control';
};

const activateMode = (tab) => {
  const mode = modes[tab.dataset.mode];
  if (!mode || !modePanel) return;
  const requestId = ++modeRequest;
  window.clearTimeout(modeTimer);
  window.clearTimeout(modeSwapTimer);
  if (modePlay?.closest('.mode-player')?.classList.contains('is-playing')) stopModePreview(false);

  modeTabs.forEach((item) => {
    const selected = item === tab;
    item.classList.toggle('is-active', selected);
    item.setAttribute('aria-selected', String(selected));
    item.tabIndex = selected ? 0 : -1;
  });

  if (reducedMotion) {
    renderMode(mode);
    return;
  }

  modePanel.classList.remove('is-changing');
  void modePanel.offsetWidth;
  modePanel.classList.add('is-changing');
  modeSwapTimer = window.setTimeout(() => {
    if (requestId === modeRequest) renderMode(mode);
  }, 350);
  modeTimer = window.setTimeout(() => modePanel.classList.remove('is-changing'), 760);
};

modeTabs.forEach((tab, index) => {
  tab.tabIndex = index === 0 ? 0 : -1;
  tab.addEventListener('click', () => activateMode(tab));
  tab.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % modeTabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + modeTabs.length) % modeTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = modeTabs.length - 1;
    modeTabs[nextIndex].focus();
    activateMode(modeTabs[nextIndex]);
  });
});

const modePlay = $('[data-mode-play]');
let modePlayTimer;
let modePlayFrame;

const initialModeTab = modeTabs.find((tab) => tab.classList.contains('is-active')) || modeTabs[0];
if (initialModeTab && modePanel && modeImage && modeIndex && modeTitle && modeCopy && modeDuration) {
  renderMode(modes[initialModeTab.dataset.mode]);
}

const stopDeviceVibration = () => {
  if (typeof navigator.vibrate !== 'function') return;
  try { navigator.vibrate(0); } catch {}
};

const stopModePreview = (complete = false) => {
  const player = modePlay?.closest('.mode-player');
  window.cancelAnimationFrame(modePlayFrame);
  window.clearTimeout(modePlayTimer);
  stopDeviceVibration();
  player?.classList.remove('is-playing');
  modePanel?.classList.remove('is-previewing');
  player?.style.setProperty('--play-progress', complete ? '1' : '0');
  if (modePlay) {
    modePlay.querySelector('span').textContent = '\u25B6';
    modePlay.setAttribute('aria-label', 'Preview touch control');
  }
  if (modeStatus) modeStatus.textContent = complete ? 'Control preview complete' : 'Ready to test control';
  if (complete) {
    modePlayTimer = window.setTimeout(() => {
      player?.style.setProperty('--play-progress', '0');
      if (modeStatus) modeStatus.textContent = 'Ready to replay control';
    }, 900);
  }
};

modePlay?.addEventListener('click', () => {
  const player = modePlay.closest('.mode-player');
  if (player.classList.contains('is-playing')) {
    stopModePreview(false);
    return;
  }

  const modeDefinition = modes[modePanel?.dataset.modeState] || modes.music;
  const duration = reducedMotion ? 180 : modeDefinition.previewMs;
  const durationSeconds = duration / 1000;
  const startedAt = performance.now();
  player.classList.add('is-playing');
  modePanel?.classList.add('is-previewing');
  player.style.setProperty('--play-progress', '0');
  modePlay.querySelector('span').textContent = '\u2161';
  modePlay.setAttribute('aria-label', 'Stop touch-control preview');
  if (modeStatus) modeStatus.textContent = `Control preview · ${durationSeconds.toFixed(1)}s`;
  try { navigator.vibrate?.(modeDefinition.vibration); } catch {}

  const updateModePreview = (now) => {
    const progress = clamp((now - startedAt) / duration);
    player.style.setProperty('--play-progress', String(progress));
    if (modeStatus && !reducedMotion) modeStatus.textContent = `Control preview · ${Math.max(0, durationSeconds * (1 - progress)).toFixed(1)}s`;
    if (progress < 1) modePlayFrame = window.requestAnimationFrame(updateModePreview);
    else stopModePreview(true);
  };
  modePlayFrame = window.requestAnimationFrame(updateModePreview);
});

if (modePanel && finePointer && !reducedMotion) {
  let modePointerFrame = 0;
  let modePointerX = 0;
  let modePointerY = 0;
  let modePointerRect = null;
  const renderModePointer = () => {
    modePointerFrame = 0;
    modePanel.style.setProperty('--mode-x', `${modePointerX * -16}px`);
    modePanel.style.setProperty('--mode-y', `${modePointerY * -10}px`);
    modePanel.style.setProperty('--mode-tilt-x', `${modePointerY * -11}deg`);
    modePanel.style.setProperty('--mode-tilt-y', `${modePointerX * 13}deg`);
    modePanel.style.setProperty('--mode-light-x', `${72 + modePointerX * 24}%`);
    modePanel.style.setProperty('--mode-light-y', `${46 + modePointerY * 26}%`);
    modePanel.style.setProperty('--mode-glint-x', `${modePointerX * 34}px`);
    modePanel.style.setProperty('--mode-glint-y', `${modePointerY * 22}px`);
  };
  const requestModePointer = () => {
    if (!modePointerFrame) modePointerFrame = window.requestAnimationFrame(renderModePointer);
  };
  modePanel.addEventListener('pointerenter', () => {
    modePointerRect = modePanel.getBoundingClientRect();
    modePanel.classList.add('is-pointer-active');
  }, { passive: true });
  modePanel.addEventListener('pointermove', (event) => {
    const rect = modePointerRect || modePanel.getBoundingClientRect();
    modePointerX = (event.clientX - rect.left) / rect.width - .5;
    modePointerY = (event.clientY - rect.top) / rect.height - .5;
    requestModePointer();
  }, { passive: true });
  modePanel.addEventListener('pointerleave', () => {
    modePointerX = 0;
    modePointerY = 0;
    modePointerRect = null;
    modePanel.classList.remove('is-pointer-active');
    requestModePointer();
  });
}

/* Exploratory sensor hardware view -------------------------------------------- */

const sensorDetails = {
  optical: {
    index: '01 / SENSING',
    title: 'Optical wellness sensing',
    copy: 'The HX3917 supports pulse-wave signals used for heart-rate, HRV and blood-oxygen wellness trends.'
  },
  motion: {
    index: '02 / CONTEXT',
    title: 'Motion context',
    copy: 'Movement sensing adds steps, activity, stillness and sleep context to your daily wellness record.'
  },
  haptic: {
    index: '03 / REMINDER',
    title: 'Private haptic cues',
    copy: 'Ring One can deliver distinct vibration cues for calls, messages, wellness alerts and everyday reminders.'
  }
};

const sensorStage = $('[data-sensor-stage]');
const sensorHotspots = $$('[data-sensor-hotspot]');
const sensorFocus = $('.sensor-focus');
const sensorIndex = $('[data-sensor-index]');
const sensorTitle = $('[data-sensor-title]');
const sensorCopy = $('[data-sensor-copy]');
let sensorInspectTimer;

const activateSensor = (button) => {
  const key = button?.dataset.sensorHotspot;
  const detail = sensorDetails[key];
  if (!detail || !sensorStage) return;
  sensorStage.dataset.sensor = key;
  sensorHotspots.forEach((hotspot) => {
    const selected = hotspot === button;
    hotspot.classList.toggle('is-active', selected);
    hotspot.setAttribute('aria-pressed', String(selected));
  });
  if (sensorIndex) sensorIndex.textContent = detail.index;
  if (sensorTitle) sensorTitle.textContent = detail.title;
  if (sensorCopy) sensorCopy.textContent = detail.copy;
  sensorFocus?.classList.remove('is-updating');
  void sensorFocus?.offsetWidth;
  sensorFocus?.classList.add('is-updating');
  sensorStage.classList.remove('is-inspecting');
  void sensorStage.offsetWidth;
  sensorStage.classList.add('is-inspecting');
  window.clearTimeout(sensorInspectTimer);
  sensorInspectTimer = window.setTimeout(() => sensorStage.classList.remove('is-inspecting'), 760);
};

sensorHotspots.forEach((button) => {
  button.addEventListener('click', () => activateSensor(button));
  button.addEventListener('focus', () => activateSensor(button));
  if (finePointer) button.addEventListener('pointerenter', () => activateSensor(button));
});

if (sensorStage && finePointer && !reducedMotion) {
  let sensorPointerFrame = 0;
  let sensorPointerClientX = 0;
  let sensorPointerClientY = 0;
  const renderSensorPointer = () => {
    sensorPointerFrame = 0;
    const rect = sensorStage.getBoundingClientRect();
    const x = clamp((sensorPointerClientX - rect.left) / rect.width);
    const y = clamp((sensorPointerClientY - rect.top) / rect.height);
    sensorStage.style.setProperty('--sensor-pointer-x', `${(x - .5) * 13}px`);
    sensorStage.style.setProperty('--sensor-pointer-y', `${(y - .5) * 10}px`);
    sensorStage.style.setProperty('--sensor-light-x', `${x * 100}%`);
    sensorStage.style.setProperty('--sensor-light-y', `${y * 100}%`);
  };
  sensorStage.addEventListener('pointermove', (event) => {
    sensorPointerClientX = event.clientX;
    sensorPointerClientY = event.clientY;
    if (!sensorPointerFrame) sensorPointerFrame = window.requestAnimationFrame(renderSensorPointer);
  }, { passive: true });
  sensorStage.addEventListener('pointerleave', () => {
    window.cancelAnimationFrame(sensorPointerFrame);
    sensorPointerFrame = 0;
    sensorStage.style.removeProperty('--sensor-pointer-x');
    sensorStage.style.removeProperty('--sensor-pointer-y');
    sensorStage.style.removeProperty('--sensor-light-x');
    sensorStage.style.removeProperty('--sensor-light-y');
  });
}

/* In-view metric storytelling -------------------------------------------------- */

const countTarget = $('[data-count-to]');
const runCountUp = () => {
  if (!countTarget || countTarget.dataset.counted === 'true') return;
  countTarget.dataset.counted = 'true';
  const target = Number.parseInt(countTarget.dataset.countTo || '0', 10);
  countTarget.closest('.sleep-score')?.classList.add('is-counted');
  if (reducedMotion) {
    countTarget.textContent = String(target);
    return;
  }
  const startedAt = performance.now();
  const drawCount = (now) => {
    const progress = clamp((now - startedAt) / 1050);
    const eased = 1 - Math.pow(1 - progress, 3);
    countTarget.textContent = String(Math.round(target * eased)).padStart(2, '0');
    if (progress < 1) window.requestAnimationFrame(drawCount);
  };
  window.requestAnimationFrame(drawCount);
};

if (countTarget) {
  const countObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    runCountUp();
    countObserver.disconnect();
  }, { threshold: .55 });
  countObserver.observe(countTarget);
}

const sleepSection = $('#sleep');
if (sleepSection) {
  if (reducedMotion) {
    sleepSection.classList.add('is-resting');
  } else {
    const sleepObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      sleepSection.classList.add('is-resting');
      sleepObserver.disconnect();
    }, { threshold: .28 });
    sleepObserver.observe(sleepSection);
  }
}

const comparisonTable = $('[data-comparison-table]');
if (comparisonTable && finePointer && !reducedMotion) {
  comparisonTable.addEventListener('pointerenter', () => comparisonTable.classList.add('is-scanning'));
  comparisonTable.addEventListener('pointermove', (event) => {
    const rect = comparisonTable.getBoundingClientRect();
    const y = clamp((event.clientY - rect.top) / rect.height);
    comparisonTable.style.setProperty('--scan-y', `${y * 100}%`);
  });
  comparisonTable.addEventListener('pointerleave', () => comparisonTable.classList.remove('is-scanning'));
}

/* Awaakii signature press-and-hold experience --------------------------------- */

const experienceStage = $('[data-experience-stage]');
const experienceControl = $('[data-experience-control]');
const experienceControlLabel = $('[data-experience-control-label]');
const experienceControlSub = $('[data-experience-control-sub]');
const experienceHint = $('[data-experience-hint]');
const experienceCount = $('[data-experience-count]');
const experienceBody = $('[data-experience-body]');
const experienceAI = $('[data-experience-ai]');
const experienceAction = $('[data-experience-action]');
const experienceFeedback = $('[data-experience-feedback]');
const experienceInsight = $('[data-experience-insight]');
const experienceSteps = $$('[data-experience-step]');

const experiencePhases = [
  {
    key: 'sense',
    body: 'Touch detected', ai: 'Alert received', action: 'Standby', feedback: 'Ready',
    insight: 'Ring One receives an incoming call, message or wellness reminder from the connected phone.'
  },
  {
    key: 'understand',
    body: 'Source matched', ai: 'Type identified', action: 'Cue queued', feedback: 'Ready',
    insight: 'The alert category is matched with a distinct vibration cue, so you can recognize what needs attention.'
  },
  {
    key: 'intervene',
    body: 'Ring connected', ai: 'Cue identified', action: 'Motor active', feedback: 'Noticed',
    insight: 'The precision reminder motor delivers a short, private vibration without requiring another screen.'
  },
  {
    key: 'learn',
    body: 'Cue delivered', ai: 'Status synced', action: 'Complete', feedback: 'Confirmed',
    insight: 'The reminder is complete and its status stays in sync with the companion app.'
  }
];

const holdDuration = 2250;
let experienceFrame;
let experienceStartedAt = 0;
let experienceProgress = 0;
let experienceHolding = false;
let experienceFinished = false;
let experiencePhaseIndex = -1;
let experienceHapticIndex = 0;
let experiencePointerId = null;
const experienceHapticThresholds = [
  [.53, 14],
  [.79, [14, 22, 18]]
];

const setExperienceText = (element, value) => {
  if (!element || element.textContent === value) return;
  element.textContent = value;
  if (!reducedMotion) {
    element.animate(
      [{ opacity: .15, transform: 'translateY(5px)' }, { opacity: 1, transform: 'none' }],
      { duration: 360, easing: 'cubic-bezier(.16,1,.3,1)' }
    );
  }
};

const updateExperienceSteps = (activeIndex, completeAll = false) => {
  experienceSteps.forEach((step, index) => {
    step.classList.toggle('is-active', completeAll ? false : index === activeIndex);
    step.classList.toggle('is-complete', completeAll || index < activeIndex);
  });
};

const setExperiencePhase = (index) => {
  if (index === experiencePhaseIndex || !experienceStage) return;
  experiencePhaseIndex = index;
  const phase = experiencePhases[index];
  experienceStage.dataset.phase = phase.key;
  updateExperienceSteps(index);
  setExperienceText(experienceBody, phase.body);
  setExperienceText(experienceAI, phase.ai);
  setExperienceText(experienceAction, phase.action);
  setExperienceText(experienceFeedback, phase.feedback);
  setExperienceText(experienceInsight, phase.insight);
};

const renderExperienceProgress = (progress) => {
  if (!experienceStage) return;
  experienceProgress = clamp(progress);
  experienceStage.style.setProperty('--experience-progress', String(experienceProgress));
  experienceStage.style.setProperty('--experience-angle', `${experienceProgress * 360}deg`);
  experienceStage.style.setProperty('--ring-turn', `${-24 + experienceProgress * 34}deg`);
  experienceStage.style.setProperty('--ring-lift', `${10 - experienceProgress * 5}px`);
  experienceStage.style.setProperty('--ring-press-scale', String(.94 + experienceProgress * .055));
  const hapticAmplitude = .22 + experienceProgress * .58;
  experienceStage.style.setProperty('--haptic-positive', `${hapticAmplitude.toFixed(2)}px`);
  experienceStage.style.setProperty('--haptic-negative', `${(-hapticAmplitude).toFixed(2)}px`);
  experienceStage.style.setProperty('--haptic-half', `${(hapticAmplitude * .46).toFixed(2)}px`);
  experienceStage.style.setProperty('--haptic-negative-half', `${(-hapticAmplitude * .46).toFixed(2)}px`);
  if (experienceCount) {
    const elapsedSeconds = Math.min(2, Math.floor(experienceProgress * holdDuration / 1000));
    experienceCount.textContent = `00:0${elapsedSeconds}`;
  }

  if (experienceProgress < .25) setExperiencePhase(0);
  else if (experienceProgress < .52) setExperiencePhase(1);
  else if (experienceProgress < .79) setExperiencePhase(2);
  else setExperiencePhase(3);

  if (experienceHolding && typeof navigator.vibrate === 'function') {
    while (experienceHapticIndex < experienceHapticThresholds.length && experienceProgress >= experienceHapticThresholds[experienceHapticIndex][0]) {
      try { navigator.vibrate(experienceHapticThresholds[experienceHapticIndex][1]); } catch {}
      experienceHapticIndex += 1;
    }
  }
};

const resetExperience = () => {
  window.cancelAnimationFrame(experienceFrame);
  if (experienceHolding || experienceHapticIndex > 0) stopDeviceVibration();
  experienceHolding = false;
  experienceFinished = false;
  experienceProgress = 0;
  experiencePhaseIndex = -1;
  experienceHapticIndex = 0;
  experiencePointerId = null;
  experienceStage?.classList.remove('is-aborted', 'is-complete', 'is-holding', 'is-completing');
  if (experienceStage) {
    experienceStage.dataset.phase = 'idle';
    experienceStage.style.setProperty('--experience-progress', '0');
    experienceStage.style.setProperty('--experience-angle', '0deg');
    experienceStage.style.setProperty('--ring-turn', '-24deg');
    experienceStage.style.setProperty('--ring-lift', '10px');
    experienceStage.style.setProperty('--ring-press-scale', '.94');
    experienceStage.style.setProperty('--haptic-positive', '.22px');
    experienceStage.style.setProperty('--haptic-negative', '-.22px');
    experienceStage.style.setProperty('--haptic-half', '.1px');
    experienceStage.style.setProperty('--haptic-negative-half', '-.1px');
  }
  experienceControl?.classList.remove('is-holding', 'is-complete');
  updateExperienceSteps(0);
  setExperienceText(experienceControlLabel, reducedMotion ? 'Activate demo' : 'Press + hold');
  setExperienceText(experienceControlSub, 'to preview reminder');
  setExperienceText(experienceHint, reducedMotion ? 'Activate the reminder demonstration' : 'Keep holding to run the full reminder');
  setExperienceText(experienceBody, 'Waiting');
  setExperienceText(experienceAI, 'Awaiting alert');
  setExperienceText(experienceAction, 'Standby');
  setExperienceText(experienceFeedback, 'Ready');
  setExperienceText(experienceInsight, 'Hold the control to preview a screen-free Ring One reminder.');
  if (experienceCount) experienceCount.textContent = '00:00';
};

const completeExperience = () => {
  if (!experienceStage) return;
  window.cancelAnimationFrame(experienceFrame);
  experienceHolding = false;
  experienceFinished = true;
  renderExperienceProgress(1);
  experienceStage.dataset.phase = 'complete';
  experienceStage.classList.remove('is-holding', 'is-aborted');
  experienceStage.classList.add('is-complete', 'is-completing');
  experienceControl?.classList.remove('is-holding');
  experienceControl?.classList.add('is-complete');
  updateExperienceSteps(3, true);
  setExperienceText(experienceControlLabel, 'Reminder delivered');
  setExperienceText(experienceControlSub, 'press to replay');
  setExperienceText(experienceHint, 'Press again to replay the reminder');
  setExperienceText(experienceBody, 'Cue noticed');
  setExperienceText(experienceAI, 'Alert identified');
  setExperienceText(experienceAction, 'Motor complete');
  setExperienceText(experienceFeedback, 'Confirmed');
  setExperienceText(experienceInsight, 'From alert to distinct vibration, Ring One helps you notice what matters without opening a screen.');
  if (experienceCount) experienceCount.textContent = '00:02';
  if (!reducedMotion && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate([18, 24, 30]); } catch {}
  }
};

const animateExperienceBack = () => {
  const from = experienceProgress;
  const started = performance.now();
  const duration = 460;
  const stepBack = (now) => {
    const progress = clamp((now - started) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    renderExperienceProgress(from * (1 - eased));
    if (progress < 1) experienceFrame = window.requestAnimationFrame(stepBack);
    else resetExperience();
  };
  experienceFrame = window.requestAnimationFrame(stepBack);
};

const tickExperience = (now) => {
  if (!experienceHolding) return;
  const progress = (now - experienceStartedAt) / holdDuration;
  renderExperienceProgress(progress);
  if (progress >= 1) {
    completeExperience();
    return;
  }
  experienceFrame = window.requestAnimationFrame(tickExperience);
};

const startExperience = (event) => {
  if (!experienceControl || !experienceStage || reducedMotion) return;
  if (event?.type === 'pointerdown' && event.button !== 0) return;
  if (experienceHolding) return;
  event?.preventDefault();
  if (experienceFinished) resetExperience();
  window.cancelAnimationFrame(experienceFrame);
  experienceStage.classList.remove('is-aborted', 'is-completing', 'is-complete');
  experienceStage.classList.add('is-holding');
  experienceHolding = true;
  experiencePointerId = event?.pointerId ?? null;
  experienceHapticIndex = 0;
  experienceStartedAt = performance.now() - experienceProgress * holdDuration;
  experienceControl.classList.add('is-holding');
  setExperienceText(experienceControlLabel, 'Keep holding');
  setExperienceText(experienceControlSub, 'do not release');
  setExperienceText(experienceHint, 'Ring One is responding to your touch');
  experienceFrame = window.requestAnimationFrame(tickExperience);
};

const stopExperience = (event) => {
  if (!experienceHolding || experienceFinished || reducedMotion) return;
  if (event?.pointerId !== undefined && experiencePointerId !== null && event.pointerId !== experiencePointerId) return;
  event?.preventDefault();
  experienceHolding = false;
  experiencePointerId = null;
  stopDeviceVibration();
  window.cancelAnimationFrame(experienceFrame);
  experienceControl?.classList.remove('is-holding');
  experienceStage?.classList.remove('is-aborted', 'is-holding');
  void experienceStage?.offsetWidth;
  experienceStage?.classList.add('is-aborted');
  setExperienceText(experienceControlLabel, 'Hold longer');
  setExperienceText(experienceControlSub, 'try once more');
  setExperienceText(experienceHint, 'Released early — hold to restart');
  animateExperienceBack();
};

if (experienceControl) {
  if (reducedMotion) {
    resetExperience();
    experienceControl.addEventListener('click', () => {
      if (experienceFinished) resetExperience();
      else completeExperience();
    });
  } else {
    experienceControl.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || experienceHolding) return;
      experienceControl.setPointerCapture?.(event.pointerId);
      startExperience(event);
    });
    experienceControl.addEventListener('pointermove', (event) => {
      if (!experienceHolding || event.pointerId !== experiencePointerId) return;
      const rect = experienceControl.getBoundingClientRect();
      const margin = 32;
      const outside = event.clientX < rect.left - margin || event.clientX > rect.right + margin || event.clientY < rect.top - margin || event.clientY > rect.bottom + margin;
      if (outside) stopExperience(event);
    });
    experienceControl.addEventListener('pointerup', stopExperience);
    experienceControl.addEventListener('pointercancel', stopExperience);
    experienceControl.addEventListener('lostpointercapture', stopExperience);
    experienceControl.addEventListener('keydown', (event) => {
      if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) startExperience(event);
      if (event.key === 'Escape' && experienceHolding) stopExperience(event);
    });
    experienceControl.addEventListener('keyup', (event) => {
      if (event.key === ' ' || event.key === 'Enter') stopExperience(event);
    });
    resetExperience();
  }
}

window.addEventListener('blur', () => {
  if (experienceHolding) stopExperience();
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden && experienceHolding) stopExperience();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && experienceHolding) stopExperience(event);
});

if (mobileBuy && $('#experience')) {
  const experienceVisibility = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      mobileBuy.classList.toggle('is-suppressed', entry.isIntersecting);
      if (!entry.isIntersecting && experienceHolding) stopExperience();
    });
  }, { threshold: .18 });
  experienceVisibility.observe($('#experience'));
}

/* FAQ and early-access form ---------------------------------------------------- */

$$('.faq-item button').forEach((button) => {
  button.addEventListener('click', () => {
    const item = button.closest('.faq-item');
    const open = !item.classList.contains('is-open');
    $$('.faq-item').forEach((faq) => {
      faq.classList.remove('is-open');
      $('button', faq)?.setAttribute('aria-expanded', 'false');
    });
    if (open) {
      item.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
    }
  });
});

$$('[data-year]').forEach((element) => {
  element.textContent = new Date().getFullYear();
});

if (document.body.classList.contains('shopify-design-mode')) {
  document.addEventListener('shopify:section:load', () => window.location.reload());
}
